import React, { useState, useEffect } from 'react';
import {
    Calendar,
    Clock,
    Users,
    BookOpen,
    CheckCircle,
    XCircle,
    AlertCircle,
    Plus,
    Edit,
    Eye,
    Settings
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import {
    MasterSchedule,
    SchoolTime
} from '../types/schedule';
import {
    getMasterSchedules,
    getSchoolTimes,
    getActiveSchedule,
    getPendingApprovalSchedules,
    submitScheduleForApproval,
    approveSchedule,
    rejectSchedule,
    activateSchedule,
    addMasterSchedule
} from '../utils/schedule';
import { getClasses } from '../utils/subjectsClasses';
import { Class } from '../utils/subjectsClasses';
import { MasterSchedule as MasterScheduleType } from '../types/schedule';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import SchoolTimeConfig from '../components/schedule/SchoolTimeConfig';
import ScheduleBuilder from '../components/schedule/ScheduleBuilder';
import ProfessionalScheduleBuilder from '../components/schedule/ProfessionalScheduleBuilder';

const ScheduleManagement: React.FC = () => {
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();

    // State
    const [schedules, setSchedules] = useState<MasterSchedule[]>([]);
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
    const [classes, setClasses] = useState<Class[]>([]);
    const [activeSchedule, setActiveSchedule] = useState<MasterSchedule | null>(null);
    const [pendingSchedules, setPendingSchedules] = useState<MasterSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'create' | 'approve' | 'school-time'>('create');

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showSchoolTimeModal, setShowSchoolTimeModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showScheduleDetailsModal, setShowScheduleDetailsModal] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState<MasterSchedule | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [schedulesData, schoolTimesData, classesData, activeScheduleData, pendingData] = await Promise.all([
                getMasterSchedules(),
                getSchoolTimes(),
                getClasses(),
                getActiveSchedule(),
                getPendingApprovalSchedules()
            ]);

            setSchedules(schedulesData);
            setSchoolTimes(schoolTimesData);
            setClasses(classesData);
            setActiveSchedule(activeScheduleData);
            setPendingSchedules(pendingData);
        } catch (error) {
            console.error('Error fetching data:', error);
            showError('Error', 'Failed to fetch schedule data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitForApproval = async (scheduleId: string) => {
        try {
            await submitScheduleForApproval(scheduleId);
            showSuccess('Submitted', 'Schedule submitted for principal approval');
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to submit schedule for approval');
        }
    };

    const handleApproveSchedule = async (scheduleId: string, effectiveDate: Date) => {
        try {
            await approveSchedule(scheduleId, user?.uid || '', effectiveDate);
            showSuccess('Approved', 'Schedule approved successfully');
            setShowApprovalModal(false);
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to approve schedule');
        }
    };

    const handleRejectSchedule = async (scheduleId: string, reason: string) => {
        try {
            await rejectSchedule(scheduleId, user?.uid || '', reason);
            showSuccess('Rejected', 'Schedule rejected');
            setShowApprovalModal(false);
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to reject schedule');
        }
    };

    const handleCreateSchedule = async (scheduleData: Omit<MasterScheduleType, 'id' | 'createdAt' | 'updatedAt'>) => {
        try {
            await addMasterSchedule({
                ...scheduleData,
                createdBy: user?.uid || ''
            });
            showSuccess('Created', 'Schedule created successfully');
            setShowCreateModal(false);
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to create schedule');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'draft':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Draft</span>;
            case 'pending_approval':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending Approval</span>;
            case 'approved':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
            case 'rejected':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
            case 'active':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">Active</span>;
            default:
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'draft':
                return <Edit className="h-4 w-4 text-gray-600" />;
            case 'pending_approval':
                return <AlertCircle className="h-4 w-4 text-yellow-600" />;
            case 'approved':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'rejected':
                return <XCircle className="h-4 w-4 text-red-600" />;
            case 'active':
                return <CheckCircle className="h-4 w-4 text-emerald-600" />;
            default:
                return <Calendar className="h-4 w-4 text-gray-600" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Schedule Management</h2>
                        <p className="text-gray-600">Manage class schedules and school timings</p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    {user?.role === 'admin' && (
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center space-x-2"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Create Schedule</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {user?.role === 'admin' && (
                        <>
                            <button
                                onClick={() => setActiveTab('create')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'create'
                                    ? 'border-emerald-500 text-emerald-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Plus className="h-4 w-4 inline mr-2" />
                                Create Schedule
                            </button>
                            <button
                                onClick={() => setActiveTab('school-time')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'school-time'
                                    ? 'border-emerald-500 text-emerald-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Clock className="h-4 w-4 inline mr-2" />
                                School Time
                            </button>
                        </>
                    )}
                    {user?.role === 'principal' && (
                        <button
                            onClick={() => setActiveTab('approve')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'approve'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <CheckCircle className="h-4 w-4 inline mr-2" />
                            Approve ({pendingSchedules.length})
                        </button>
                    )}
                </nav>
            </div>

            {/* Content */}
            {activeTab === 'create' && user?.role === 'admin' && (
                <ProfessionalScheduleBuilder
                    onSave={handleCreateSchedule}
                    onCancel={() => setActiveTab('school-time')}
                    schoolTimes={schoolTimes}
                />
            )}

            {activeTab === 'school-time' && user?.role === 'admin' && (
                <SchoolTimeConfig />
            )}

            {activeTab === 'approve' && user?.role === 'principal' && (
                <div className="space-y-6">
                    <Card glassy={true} className="islamic-pattern">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Approval</h3>
                            {pendingSchedules.length === 0 ? (
                                <div className="text-center py-8">
                                    <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">No pending approvals</h4>
                                    <p className="text-gray-600">All schedules have been reviewed.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {pendingSchedules.map((schedule) => (
                                        <div key={schedule.id} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <div className="flex items-center justify-between mb-3">
                                                <div>
                                                    <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                                                    <p className="text-sm text-gray-600">{schedule.description}</p>
                                                </div>
                                                {getStatusBadge(schedule.status)}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedSchedule(schedule);
                                                        setShowApprovalModal(true);
                                                    }}
                                                >
                                                    Review & Approve
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedSchedule(schedule);
                                                        setShowScheduleDetailsModal(true);
                                                    }}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}

            {/* Approval Modal */}
            {showApprovalModal && selectedSchedule && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowApprovalModal(false)}
                    title={`Approve Schedule - ${selectedSchedule.name}`}
                    size="md"
                >
                    <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-900 mb-2">Schedule Details</h4>
                            <p className="text-sm text-blue-700">{selectedSchedule.description}</p>
                            <p className="text-sm text-blue-700 mt-1">
                                Classes: {selectedSchedule.schedules.length}
                            </p>
                        </div>

                        <div className="flex space-x-3">
                            <Button
                                onClick={() => {
                                    const effectiveDate = new Date();
                                    effectiveDate.setDate(effectiveDate.getDate() + 1); // Tomorrow
                                    handleApproveSchedule(selectedSchedule.id, effectiveDate);
                                }}
                                className="flex-1"
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    const reason = prompt('Rejection reason:');
                                    if (reason) {
                                        handleRejectSchedule(selectedSchedule.id, reason);
                                    }
                                }}
                                className="flex-1"
                            >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Create Schedule Modal */}
            {showCreateModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowCreateModal(false)}
                    title="Create New Schedule"
                    size="lg"
                >
                    <ProfessionalScheduleBuilder
                        onSave={handleCreateSchedule}
                        onCancel={() => setShowCreateModal(false)}
                        schoolTimes={schoolTimes}
                    />
                </Modal>
            )}

            {/* Schedule Details Modal */}
            {showScheduleDetailsModal && selectedSchedule && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowScheduleDetailsModal(false)}
                    title={`Schedule Details - ${selectedSchedule.name}`}
                    size="lg"
                >
                    <div className="space-y-6">
                        {/* Schedule Info */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-medium text-gray-900 mb-2">Schedule Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-medium text-gray-700">Name:</span>
                                    <p className="text-gray-900">{selectedSchedule.name}</p>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <div className="mt-1">{getStatusBadge(selectedSchedule.status)}</div>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-700">Description:</span>
                                    <p className="text-gray-900">{selectedSchedule.description}</p>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-700">Created:</span>
                                    <p className="text-gray-900">{selectedSchedule.createdAt.toLocaleDateString()}</p>
                                </div>
                                {selectedSchedule.effectiveDate && (
                                    <div>
                                        <span className="font-medium text-gray-700">Effective Date:</span>
                                        <p className="text-gray-900">{selectedSchedule.effectiveDate.toLocaleDateString()}</p>
                                    </div>
                                )}
                                {selectedSchedule.approvedAt && (
                                    <div>
                                        <span className="font-medium text-gray-700">Approved:</span>
                                        <p className="text-gray-900">{selectedSchedule.approvedAt.toLocaleDateString()}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Class Schedules */}
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Class Schedules ({selectedSchedule.schedules.length})</h4>
                            <div className="space-y-3">
                                {selectedSchedule.schedules.map((classSchedule, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h5 className="font-medium text-gray-900">{classSchedule.className}</h5>
                                            <span className="text-sm text-gray-600">{classSchedule.periods.length} periods</span>
                                        </div>

                                        {/* Periods */}
                                        {classSchedule.periods.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                {classSchedule.periods.map((period, periodIndex) => (
                                                    <div key={periodIndex} className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                                                        <div className="font-medium text-emerald-900">{period.subject}</div>
                                                        <div className="text-emerald-700">{period.teacherName}</div>
                                                        <div className="text-emerald-600">
                                                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][period.dayOfWeek]}
                                                            {period.startTime} - {period.endTime}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No periods scheduled</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            {user?.role === 'principal' && selectedSchedule.status === 'pending_approval' && (
                                <>
                                    <Button
                                        onClick={() => {
                                            setShowScheduleDetailsModal(false);
                                            setShowApprovalModal(true);
                                        }}
                                    >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Review & Approve
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="secondary"
                                onClick={() => setShowScheduleDetailsModal(false)}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ScheduleManagement;
